/** The frame every Story is composed into. */
export const STORY_WIDTH = 1080;
export const STORY_HEIGHT = 1920;
export const STORY_RATIO = STORY_WIDTH / STORY_HEIGHT;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/**
 * Turns a picked file into a data URL, rotated a quarter turn at a time.
 *
 * Rotation is baked into the pixels rather than applied as a CSS transform, so
 * the preview and the exported frame are looking at the same image and the
 * framing the member chose is the framing that gets published.
 */
export const rotateImage90 = async (src, direction = "right") => {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  // A quarter turn swaps the axes.
  canvas.width = img.naturalHeight;
  canvas.height = img.naturalWidth;

  const ctx = canvas.getContext("2d");
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(((direction === "left" ? -90 : 90) * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  return canvas.toDataURL("image/jpeg", 0.92);
};

/**
 * How much of the image spills outside the 9:16 frame once it covers it.
 *
 * Returns the scale plus the slack on each axis, which is what both the drag
 * and the export need: a portrait phone photo has vertical slack, a landscape
 * one has horizontal slack, and a 9:16 photo has neither.
 */
export const coverGeometry = (naturalWidth, naturalHeight) => {
  const scale = Math.max(STORY_WIDTH / naturalWidth, STORY_HEIGHT / naturalHeight);
  const drawnWidth = naturalWidth * scale;
  const drawnHeight = naturalHeight * scale;

  return {
    scale,
    drawnWidth,
    drawnHeight,
    slackX: Math.max(0, drawnWidth - STORY_WIDTH),
    slackY: Math.max(0, drawnHeight - STORY_HEIGHT),
  };
};

/**
 * Renders the framed photo into a 1080×1920 JPEG.
 *
 * `offset` is 0–1 per axis, the same number the preview uses, so what the
 * member dragged is what lands in the file. Only the photo is drawn: the
 * caption stays structured data the viewer lays out, which is what lets it be
 * edited later and keeps it legible at any screen size.
 */
export const renderStoryFrame = async (src, offset = { x: 0.5, y: 0.5 }) => {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = STORY_WIDTH;
  canvas.height = STORY_HEIGHT;

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);

  const { drawnWidth, drawnHeight, slackX, slackY } = coverGeometry(
    img.naturalWidth,
    img.naturalHeight,
  );

  ctx.drawImage(img, -slackX * offset.x, -slackY * offset.y, drawnWidth, drawnHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Story encode failed"));
          return;
        }
        resolve(new File([blob], "story.jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.9,
    );
  });
};
